import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import React, { Dispatch, KeyboardEvent, SetStateAction, TouchEvent, useRef, useState } from 'react';

import { ActionIcon } from '@/components/ActionIcon';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconClose24 } from '@cineamo/ui/src/icons/ic-close';

import { useRouter } from '@/i18n/routing';

export type ModalProps = {
    children?: React.ReactNode;
    className?: string;
    title?: string | React.JSX.Element;
    titleCenter?: boolean;
    titleClassName?: string;
    subTitle?: string | React.JSX.Element;
    subTitleClassName?: string;
    modalIsOpen?: boolean;
    setModalIsOpen?: Dispatch<SetStateAction<boolean>>;
    closeable?: boolean;
    showCloseButton?: boolean;
    showBackground?: boolean;
    showBackgroundBlur?: boolean;
    enableOutsideActions?: boolean;
    onClose?: (() => void) | null;
    beforeClose?: () => boolean | void;
};

function Modal(props: ModalProps): React.JSX.Element | null {
    const { className, onClose, beforeClose } = props;

    const t = useTranslations();

    const router = useRouter();

    const closeButton = useRef(null);

    let modalIsOpen = props?.modalIsOpen;
    let setModalIsOpen = props?.setModalIsOpen;
    const [state, setState] = useState<boolean>(true);
    if (!modalIsOpen && !setModalIsOpen) {
        modalIsOpen = state;
        setModalIsOpen = setState;
    }

    const closeable = props.closeable !== false;

    function onCloseInternal() {
        if (beforeClose && beforeClose() === false) {
            return;
        }
        setModalIsOpen?.(false);
        if (onClose) {
            onClose();
        } else if (onClose === undefined) {
            router.back();
        }
    }

    if (modalIsOpen === false) {
        return null;
    }

    return (
        <Dialog
            initialFocus={closeButton}
            open={modalIsOpen}
            onClose={() => {
                if (closeable) {
                    onCloseInternal();
                }
            }}
            onKeyDown={(e: KeyboardEvent<HTMLDivElement> & { enterKey?: number }) => {
                const key = e.which || e.keyCode;
                if (key === e.enterKey || key === 13) {
                    e.preventDefault();
                }
            }}
            className={cn('fixed z-modal-view inset-0 w-screen h-full', {
                'pointer-events-none': props.enableOutsideActions
            })}>
            <DialogBackdrop
                className={cn('fixed inset-0 flex items-center justify-center w-full h-full py-22 md:p-24', {
                    'backdrop-blur-sm': props.showBackgroundBlur !== false
                })}
                onTouchStart={(e: TouchEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                }}
                onTouchMove={(e: TouchEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                }}
                onTouchEnd={(e: TouchEvent<HTMLDivElement>) => {
                    e.stopPropagation();
                }}>
                <div
                    className={cn(
                        'absolute inset-0 transition-all duration-300 bg-[var(--background-color)] opacity-30 brightness-25'
                    )}
                />
            </DialogBackdrop>

            {/* If justify center is needed pass it down from where you need it */}
            <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
                <DialogPanel
                    className={cn(
                        className,
                        'relative flex flex-col',
                        'w-full mx-16 max-h-mobile-modal',
                        'sm:h-auto sm:min-h-0 sm:max-h-full',
                        'modal-window',
                        'rounded-8 overflow-hidden',
                        'p-0',
                        'bg-[var(--background-color)] text-[var(--on-surface-color)]',
                        {
                            'pointer-events-auto': props.enableOutsideActions
                        }
                    )}>
                    {/* `min-h-0` is what lets the panel's max-height actually reach the children.
                        Without it this flex item keeps `min-height: auto`, refuses to shrink below
                        its content, and overflows the `overflow-hidden` panel — so a tall child
                        gets clipped and no scroll container inside it can ever work. */}
                    <div className="w-full h-full min-h-0 grow flex flex-col p-8 md:p-24 bg-[var(--surface-color)]">
                        {(props.title || props.subTitle) && (
                            <DialogTitle
                                className={cn(
                                    'relative flex-none pb-18 md:pb-16 mt-24 md:mt-0',
                                    props.titleClassName || 'text-headline-24',
                                    {
                                        'text-center mx-auto': props.titleCenter
                                    }
                                )}>
                                {props.title}
                                {props.subTitle && (
                                    <div
                                        className={cn(
                                            props.subTitleClassName || 'text-body-14 tracking-wide',
                                            'text-[var(--on-surface-color)]'
                                        )}>
                                        {props.subTitle}
                                    </div>
                                )}
                            </DialogTitle>
                        )}

                        {closeable && props.showCloseButton !== false && (
                            <div className="absolute right-8 top-8 md:top-24 md:right-24 text-gray-middle">
                                <ActionIcon
                                    icon={<IconClose24 className="opacity-60 text-[var(--on-surface-color)]" />}
                                    action={onCloseInternal}
                                    // tooltipDirection={TooltipDirection.Left}
                                    tooltipText={t('common.close')}
                                />
                            </div>
                        )}

                        {props.children}
                    </div>
                </DialogPanel>
            </div>
        </Dialog>
    );
}

export default Modal;
